"use client";
import type * as PhosphorIcons from "@phosphor-icons/react";
import type { IconProps as PhosphorIconProps } from "@phosphor-icons/react";
import { lazy, memo, Suspense } from "react";
import { twMerge } from "tailwind-merge";
import { CUSTOM_ICON_REGISTRY, type CustomIconName } from "./customIcons";
import { phosphorNames } from "./phosphorNames.generated";
import { getPhosphorIcon, loadPhosphorIcons } from "./phosphorRegistry";

const availableNames = new Set<string>(phosphorNames);
const DeferredPhosphorIcon = lazy(async () => {
  try {
    await loadPhosphorIcons();
  } catch {
    try {
      await loadPhosphorIcons();
    } catch (error) {
      console.error("Failed to load Phosphor icons", error);
      return { default: () => null };
    }
  }
  return {
    default: ({ name, ...props }: PhosphorIconProps & { name: string }) => {
      const Component = getPhosphorIcon(name);
      return Component ? <Component {...props} /> : null;
    },
  };
});

// Type for any Phosphor icon component
type PhosphorIconComponent = typeof PhosphorIcons.House;

// Extract all icon names from the Phosphor package
export type PhosphorIconName = keyof typeof PhosphorIcons;

// Re-export custom icon names so consumers can reference the union directly.
export type { CustomIconName } from "./customIcons";

/**
 * All icon names accepted by `<Icon name="…" />` — every Phosphor icon plus
 * Texture's custom icons (see `customIcons/registry.tsx`). Custom names are
 * prefixed `Texture*` and resolve through `CUSTOM_ICON_REGISTRY` first, then
 * Phosphor.
 */
export type IconName = PhosphorIconName | CustomIconName;

// Dev-time guard: a custom icon name must never collide with a Phosphor name,
// otherwise a future Phosphor release could silently shadow (or be shadowed by)
// a custom glyph. The `Texture*` prefix convention prevents this; this assert
// makes a violation loud during development instead of a silent wrong-icon.
if (process.env.NODE_ENV === "development") {
  for (const customName of Object.keys(CUSTOM_ICON_REGISTRY)) {
    if (availableNames.has(customName)) {
      console.error(
        `Custom icon "${customName}" collides with a Phosphor icon of the same name. ` +
          `Rename it (use the "Texture" prefix) to keep the icon namespace unambiguous.`
      );
    }
  }
}

// Standard size presets for the design system
const sizePresets = {
  xs: 16,
  sm: 20,
  md: 24,
  lg: 32,
  xl: 40,
  "2xl": 48,
} as const;

type SizePreset = keyof typeof sizePresets;

interface IconProps extends Omit<PhosphorIconProps, "size"> {
  /**
   * The name of the icon to render — any valid icon from @phosphor-icons/react,
   * or a Texture custom icon (see customIcons/registry.tsx).
   */
  name: IconName;

  /**
   * Size of the icon - can be a preset or custom number
   */
  size?: SizePreset | number;

  /**
   * Accessibility label (maps to aria-label)
   */
  ariaLabel?: string;
}

/**
 * Icon
 *
 * Icon wrapper component with standardized sizing and styling. Provides access
 * to the full Phosphor icon library PLUS Texture's custom icons through one
 * unified `name` API, with tree-shaking support and design system presets.
 * - TypeScript autocomplete for all icon names (Phosphor + custom)
 *
 * Custom icons resolve through `CUSTOM_ICON_REGISTRY` first, then fall back to
 * Phosphor — so `<Icon name="TextureRecloser" />` works exactly like
 * `<Icon name="House" />`.
 *
 * Usage:
 * ```tsx
 * <Icon name="House" size="md" />
 * <Icon name="TextureRecloser" size={32} className="text-brand" />
 * ```
 */
export const Icon = memo(
  ({ name, size = "md", color, weight = "regular", className, ariaLabel, ...props }: IconProps) => {
    // Resolve the icon: custom registry takes precedence, then Phosphor.
    const IconComponent =
      (CUSTOM_ICON_REGISTRY[name as CustomIconName] as PhosphorIconComponent | undefined) ?? getPhosphorIcon(name);

    const canonicalName = name.endsWith("Icon") ? name.slice(0, -4) : name;
    if (!IconComponent && !availableNames.has(canonicalName)) {
      if (process.env.NODE_ENV === "development") {
        console.error(`Icon "${name}" not found in @phosphor-icons/react or the custom icon registry`);
      }
      return null;
    }

    // Resolve size to pixel value
    const resolvedSize = typeof size === "string" && size in sizePresets ? sizePresets[size as SizePreset] : size;

    // Build className with design system defaults
    const iconClassName = twMerge(
      // Default styles
      "inline-block flex-shrink-0",
      // Allow color inheritance from parent
      !color && "text-current",
      // Custom classes
      className
    );

    const iconProps = {
      ...props,
      size: resolvedSize as number,
      color,
      weight,
      className: iconClassName,
      "aria-label": ariaLabel || `${name} icon`,
      "data-testid": `icon-${name}`,
    };
    if (IconComponent) return <IconComponent {...iconProps} />;
    return (
      <Suspense
        fallback={
          <svg
            width={resolvedSize}
            height={resolvedSize}
            className={iconClassName}
            aria-hidden="true"
            data-icon-loading=""
          />
        }
      >
        <DeferredPhosphorIcon name={name} {...iconProps} />
      </Suspense>
    );
  }
);

Icon.displayName = "Icon";

/**
 * Helper component for icons with containers
 */
interface IconWithContainerProps extends IconProps {
  /**
   * Container variant
   */
  variant?: "subtle" | "solid" | "outline";

  /**
   * Container shape
   */
  shape?: "square" | "circle";

  /**
   * Container size (adds padding around icon)
   */
  containerSize?: SizePreset | number;

  /**
   * Container background color
   */
  containerClassName?: string;
}

export const IconWithContainer = memo(
  ({
    variant = "subtle",
    shape = "square",
    containerSize,
    containerClassName,
    size = "md",
    className,
    name,
    color,
    weight,
    ariaLabel,
    ref,
    ...props
  }: IconWithContainerProps & { ref?: React.Ref<HTMLDivElement> }) => {
    // Calculate container size if not specified
    const iconSize = typeof size === "string" && size in sizePresets ? sizePresets[size as SizePreset] : size;
    const resolvedContainerSize = containerSize
      ? typeof containerSize === "string" && containerSize in sizePresets
        ? sizePresets[containerSize as SizePreset]
        : containerSize
      : (iconSize as number) * 1.75; // Default to 1.75x icon size

    const containerClasses = twMerge(
      // Base styles
      "inline-flex items-center justify-center flex-shrink-0",

      // Shape
      shape === "circle" ? "rounded-full" : "rounded-lg",

      // Variant styles using Tailwind 4 CSS variables
      variant === "subtle" && "bg-background-muted",
      variant === "solid" && "bg-action-brand text-[color:var(--color-action-brand-text)]",
      variant === "outline" && "border-2 border-current",

      // Container className for overrides
      containerClassName
    );

    return (
      <div
        className={containerClasses}
        style={{
          width: resolvedContainerSize,
          height: resolvedContainerSize,
        }}
      >
        <Icon
          {...props}
          name={name}
          size={size}
          color={color}
          weight={weight}
          className={className}
          ariaLabel={ariaLabel}
        />
      </div>
    );
  }
);

IconWithContainer.displayName = "IconWithContainer";
